import React, { useEffect, useState } from "react";
import "./RiskEngine.css";
import { analyzeLiveRisk } from "../liveTrading/NinaLiveRiskEngine";

function RiskEngine({
  demoMarketState,
  trade,
  liveDecision,
  plannedQuantity,
  variant = "full",
}) {
 const [accountSize, setAccountSize] = useState(() => {
  return Number(
    localStorage.getItem("aninaAccountSize")
  ) || 10000;
}); 

useEffect(() => {
  localStorage.setItem(
    "aninaAccountSize",
    accountSize
  );
}, [accountSize]);

useEffect(() => {
  const handleAccountSizeChange = (event) => {
    const nextAccountSize = Number(
      event.detail?.accountSize
    );

    if (Number.isFinite(nextAccountSize)) {
      setAccountSize(nextAccountSize);
    }
  };

  window.addEventListener(
    "nina:account-size-change",
    handleAccountSizeChange
  );

  return () => {
    window.removeEventListener(
      "nina:account-size-change",
      handleAccountSizeChange
    );
  };
}, []);

const updateAccountSize = (nextAccountSize) => {
  setAccountSize(nextAccountSize);

  window.dispatchEvent(
    new CustomEvent("nina:account-size-change", {
      detail: {
        accountSize: nextAccountSize,
      },
    })
  );
};

  const [selectedSymbol, setSelectedSymbol] = useState(() => {
  return (
    localStorage.getItem("aninaSelectedSymbol") ||
    "PLUG"
  );
});

useEffect(() => {
  const handleSymbolChange = (event) => {
    const symbol = event.detail?.symbol;

    if (!symbol) return;

    setSelectedSymbol(symbol);
  };

  window.addEventListener(
    "nina:symbol-change",
    handleSymbolChange
  );

  return () => {
    window.removeEventListener(
      "nina:symbol-change",
      handleSymbolChange
    );
  };
}, []);

const liveRisk = analyzeLiveRisk({
  trade,
  liveDecision,
  accountSize,
});


 const isDemoActive = Boolean(demoMarketState);
  
const {
  blocked: tradeBlocked,
  riskLevel,
  riskPercent,
  maximumAllowedLoss,
  entry,
  stop,
  target1,
  target2,
  target3,
  riskPerShare,
  recommendedShares,
  positionValue,
  buyingPowerUsed,
  actualMaximumLoss,
} = liveRisk;

const managementQuantity =
  plannedQuantity === null
    ? 0
    : Number.isSafeInteger(plannedQuantity) && plannedQuantity >= 0
    ? plannedQuantity
    : recommendedShares;
const managementTarget1Shares = Math.floor(managementQuantity * 0.3);
const managementTarget2Shares = Math.floor(managementQuantity * 0.3);
const managementTarget3Shares =
  managementQuantity - managementTarget1Shares - managementTarget2Shares;
const managementTarget1Profit =
  managementTarget1Shares * ((target1 ?? 0) - (entry ?? 0));
const managementTarget2Profit =
  managementTarget2Shares * ((target2 ?? 0) - (entry ?? 0));
const managementTarget3Profit =
  managementTarget3Shares * ((target3 ?? 0) - (entry ?? 0));
const managementTotalProfit =
  managementTarget1Profit + managementTarget2Profit + managementTarget3Profit;

const target1Profit =
  recommendedShares * ((target1 ?? 0) - (entry ?? 0));

const target2Profit =
  recommendedShares * ((target2 ?? 0) - (entry ?? 0));

const target3Profit =
  recommendedShares * ((target3 ?? 0) - (entry ?? 0));


  const riskBadgeText = tradeBlocked
    ? "TRADE BLOCKED"
    : `${riskLevel.toUpperCase()} RISK`;

  const tradePermission = tradeBlocked
    ? "No Trade"
    : isDemoActive
    ? demoMarketState.nextAction
    : "Trade Allowed";

  const partialProfitStrategy = (
    <section className="partialProfitSection partialProfitStandalone">
      <div className="partialProfitHeader">
        <div>
          <span className="partialProfitEyebrow">
            AI TRADE MANAGEMENT
          </span>

          <h3>📊 Partial Profit Strategy</h3>

          <p>
            Take profits in stages while keeping part of the
            position available for a larger move.
          </p>
        </div>

        <strong>
          {tradeBlocked
            ? "30% / 30% / 40% · PREVIEW ONLY"
            : "30% / 30% / 40%"}
        </strong>
      </div>

      <div className="partialProfitGrid">
        <div className="partialProfitCard">
          <span>Target 1 Exit</span>
          <strong>{managementTarget1Shares.toLocaleString()} Shares</strong>
          <small>Sell at {formatPrice(target1)}</small>
          <b>+{formatMoney(managementTarget1Profit)}</b>
          <em>
            {(managementQuantity - managementTarget1Shares).toLocaleString()} shares remain
          </em>
        </div>

        <div className="partialProfitCard">
          <span>Target 2 Exit</span>
          <strong>{managementTarget2Shares.toLocaleString()} Shares</strong>
          <small>Sell at {formatPrice(target2)}</small>
          <b>+{formatMoney(managementTarget2Profit)}</b>
          <em>
            {(managementQuantity - managementTarget1Shares - managementTarget2Shares).toLocaleString()} shares remain
          </em>
        </div>

        <div className="partialProfitCard">
          <span>Target 3 Exit</span>
          <strong>{managementTarget3Shares.toLocaleString()} Shares</strong>
          <small>Sell at {formatPrice(target3)}</small>
          <b>+{formatMoney(managementTarget3Profit)}</b>
          <em>Position fully closed</em>
        </div>

        <div className="partialProfitTotalCard">
          <span>Total Projected Profit</span>
          <strong>
            +{formatMoney(managementTotalProfit)}
          </strong>
          <small>
            {tradeBlocked
              ? "Preview only — no trade permission"
              : "Based on staged exits"}
          </small>
        </div>
      </div>
    </section>
  );

  if (variant === "partial") {
    return partialProfitStrategy;
  }

return (
  <section
    className={`riskEngineCard ${
      variant === "risk" ? "riskEngineCompact" : ""
    }`}
    data-tour-id={
      variant === "risk" ? "account-summary" : undefined
    }
  >
      <div className="riskHeader">
        <div>
          <span className="riskEyebrow">
            {variant === "risk"
              ? "ACCOUNT SUMMARY"
              : "POSITION SIZE & CAPITAL PROTECTION"}
          </span>

          <h2>⚠️ AI Risk Engine</h2>

          <p>
  {isDemoActive
    ? `${demoMarketState.symbol} • Account-based position calculator`
    : `${selectedSymbol} • Account-based position calculator`}
</p>
        </div>

        <div className="riskBadge">
          {riskBadgeText}
        </div>
      </div>

      <div className="riskGrid">
       <div className="riskBox">
  <span>Account Size</span>

  <input
  className="accountSizeInput"
  type="text"
  min="100"
  step="100"
  value={accountSize}
 onChange={(e) => {
  const cleanedValue = e.target.value.replace(/^0+(?=\d)/, "");

  updateAccountSize(
    cleanedValue === ""
      ? 0
      : Number(cleanedValue)
  );
}}
/>
</div> 
         

        <div className="riskBox">
          <span>Risk Per Trade</span>

          <strong>
  {`${riskPercent}%`}
</strong>
        </div>

        <div className="riskBox">
          <span>Maximum Loss</span>

          <strong>
            {formatMoney(actualMaximumLoss)}
          </strong>
        </div>

        <div className="riskBox">
          <span>Recommended Shares</span>

         <strong>
  {tradeBlocked
    ? `${recommendedShares.toLocaleString()} PREVIEW — BLOCKED`
    : recommendedShares.toLocaleString()}
</strong>
        </div>
      </div>

      <div className="riskMetricsGrid">
        <div className="riskMetricCard">
          <span>Risk Per Share</span>

          <strong>
            {formatMoney(riskPerShare)}
          </strong>
        </div>

        <div className="riskMetricCard">
          <span>Position Value</span>

          <strong>
  {formatMoney(positionValue)}
</strong>
        </div>

        <div className="riskMetricCard">
          <span>Buying Power Used</span>

         <strong>
  {`${buyingPowerUsed.toFixed(1)}%`}
</strong>
        </div>

      </div>

      {variant !== "risk" && (
      <div className="profitCalculatorSection">
        <div className="profitCalculatorHeader">
          <div>
            <span className="profitEyebrow">
              POTENTIAL PROFIT CALCULATOR
            </span>

            <h3>💰 Full Position Projection</h3>
          </div>

          <strong>
            {tradeBlocked
              ? "NO TRADE"
              : `${recommendedShares.toLocaleString()} Shares`}
          </strong>
        </div>

        <div className="profitProjectionGrid">
          <div className="profitProjectionCard">
            <span>Target 1</span>

            <strong>
              {tradeBlocked
                ? "$0.00"
                : `+${formatMoney(target1Profit)}`}
            </strong>

            <small>
              Exit all shares at {formatPrice(target1)}
            </small>
          </div>

          <div className="profitProjectionCard">
            <span>Target 2</span>

            <strong>
              {tradeBlocked
                ? "$0.00"
                : `+${formatMoney(target2Profit)}`}
            </strong>

            <small>
              Exit all shares at {formatPrice(target2)}
            </small>
          </div>

          <div className="profitProjectionCard">
            <span>Target 3</span>

            <strong>
              {tradeBlocked
                ? "$0.00"
                : `+${formatMoney(target3Profit)}`}
            </strong>

            <small>
              Exit all shares at {formatPrice(target3)}
            </small>
          </div>

          <div className="lossProjectionCard">
            <span>Maximum Risk</span>

            <strong>
              {tradeBlocked
                ? "$0.00"
                : `-${formatMoney(actualMaximumLoss)}`}
            </strong>

            <small>
              Stop at {formatPrice(stop)}
            </small>
          </div>
        </div>
      </div>
      )}

      {variant !== "risk" && partialProfitStrategy}

      <div className="riskPermissionPanel">
        <div>
          <span>Trade Permission</span>

          <strong>{tradePermission}</strong>
        </div>

        <p>
          {tradeBlocked
            ? "The AI Assistant has blocked this setup to protect capital."
            : `The position uses ${recommendedShares.toLocaleString()} shares with approximately ${formatMoney(
                actualMaximumLoss
              )} of total risk.`}
        </p>
      </div>
    </section>
  );
}

function getRiskPercent(riskLevel) {
  if (riskLevel === "Low") {
    return 1;
  }

  if (riskLevel === "Moderate") {
    return 0.5;
  }

  if (riskLevel === "High") {
    return 0.25;
  }

  return 0;
}

function formatMoney(value) {
  if (typeof value !== "number") {
    return "$0.00";
  }

  return `$${value.toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

function formatPrice(price) {
  if (typeof price !== "number") {
    return "—";
  }

  return `$${price.toFixed(2)}`;
}

export default RiskEngine;

export function PartialProfitStrategy({
  demoMarketState,
  trade,
  liveDecision,
  plannedQuantity,
}) {
  return (
    <RiskEngine
      demoMarketState={demoMarketState}
      trade={trade}
      liveDecision={liveDecision}
      plannedQuantity={plannedQuantity}
      variant="partial"
    />
  );
}
